import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Stack, Paper, List, ListItem, ListItemAvatar,
  ListItemText, Avatar, TextField, Button, Chip, Divider,
  CircularProgress, Alert, Badge, IconButton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useQuery, useMutation } from '@apollo/client';
import {
  MY_CONVERSATIONS_QUERY, MESSAGES_QUERY,
  SEND_MESSAGE_MUTATION, MARK_MESSAGES_READ_MUTATION,
} from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/dateUtils';

import Paged from '../components/Paged'; // lists beyond 10 are paged
function initials(firstName, lastName, email) {
  if (firstName) return `${firstName[0]}${lastName?.[0] || ''}`.toUpperCase();
  return (email || '?')[0].toUpperCase();
}

function displayName(conv) {
  if (conv.firstName) return `${conv.firstName} ${conv.lastName || ''}`.trim();
  return conv.email;
}

export default function MessagingPage() {
  const { user: authUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeUserId, setActiveUserId] = useState(searchParams.get('with') || null);
  const [draft, setDraft] = useState('');
  const messagesEndRef = useRef(null);

  const { data: convData, loading: convLoading, error: convError, refetch: refetchConvs }
    = useQuery(MY_CONVERSATIONS_QUERY, { fetchPolicy: 'cache-and-network', pollInterval: 15000 });

  const { data: msgData, loading: msgLoading, refetch: refetchMsgs }
    = useQuery(MESSAGES_QUERY, {
        variables: { withUserId: activeUserId },
        skip: !activeUserId,
        fetchPolicy: 'cache-and-network',
        pollInterval: 5000,
      });

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE_MUTATION, {
    onCompleted: () => { setDraft(''); refetchMsgs(); refetchConvs(); },
  });

  const [markRead] = useMutation(MARK_MESSAGES_READ_MUTATION);

  useEffect(() => {
    if (activeUserId) {
      markRead({ variables: { fromUserId: activeUserId } });
    }
  }, [activeUserId, msgData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgData]);

  const conversations = convData?.myConversations || [];
  const messages      = msgData?.messages || [];
  const activeConv    = conversations.find(c => c.userId === activeUserId);

  const handleSend = () => {
    if (!draft.trim() || !activeUserId) return;
    sendMessage({ variables: { toUserId: activeUserId, body: draft.trim() } });
  };

  if (convError) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {convError.message.includes('Forbidden')
            ? 'You do not have permission to access messages. Please contact the administrator.'
            : convError.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Box mb={2}>
        <Typography variant="h4" fontWeight={700}>Messages</Typography>
        <Typography variant="body2" color="text.secondary">
          Secure family-to-family conversations
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, border: '1px solid #e0e0e0', borderRadius: 3, overflow: 'hidden' }}>

        {/* Conversation list */}
        <Paper elevation={0} sx={{ width: 300, flexShrink: 0, borderRight: '1px solid #e0e0e0', overflowY: 'auto' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #f5f5f5' }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">CONVERSATIONS</Typography>
          </Box>

          {convLoading && !conversations.length && (
            <Stack alignItems="center" py={4}><CircularProgress size={24} /></Stack>
          )}

          {!convLoading && conversations.length === 0 && (
            <Box p={3} textAlign="center">
              <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: '#bdbdbd', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No conversations yet.</Typography>
              <Typography variant="caption" color="text.secondary">
                Go to Matchmaking and send an interest — you can then start a conversation.
              </Typography>
            </Box>
          )}

          <List disablePadding>
            <Paged items={conversations}>{rows => rows.map(conv => (
              <React.Fragment key={conv.userId}>
                <ListItem
                  button
                  selected={conv.userId === activeUserId}
                  onClick={() => setActiveUserId(conv.userId)}
                  sx={{ py: 1.5, px: 2, '&.Mui-selected': { bgcolor: 'primary.50' } }}>
                  <ListItemAvatar>
                    <Badge badgeContent={conv.unreadCount || 0} color="error" max={9}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '0.9rem' }}>
                        {initials(conv.firstName, conv.lastName, conv.email)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={conv.unreadCount > 0 ? 700 : 400} noWrap>
                        {displayName(conv)}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {conv.lastMessage?.body || 'No messages yet'}
                      </Typography>
                    }
                  />
                  {conv.lastMessage && (
                    <Typography variant="caption" color="text.disabled" sx={{ ml: 1, whiteSpace: 'nowrap', fontSize: 10 }}>
                      {fmtDate(conv.lastMessage.createdAt, 'datetime').slice(0, 11)}
                    </Typography>
                  )}
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}</Paged>
          </List>
        </Paper>

        {/* Message thread */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!activeUserId ? (
            <Stack flex={1} alignItems="center" justifyContent="center" spacing={2}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 56, color: '#bdbdbd' }} />
              <Typography color="text.secondary">Select a conversation to start messaging</Typography>
            </Stack>
          ) : (
            <>
              {/* Header */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {initials(activeConv?.firstName, activeConv?.lastName, activeConv?.email)}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={700}>{activeConv ? displayName(activeConv) : activeUserId}</Typography>
                    <Typography variant="caption" color="text.secondary">{activeConv?.email}</Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {msgLoading && !messages.length && (
                  <Stack alignItems="center" mt={4}><CircularProgress size={24} /></Stack>
                )}
                {messages.map(msg => {
                  const isMine = msg.fromUser === authUser?.id;
                  return (
                    <Stack key={msg.id} direction="row" justifyContent={isMine ? 'flex-end' : 'flex-start'} mb={1.5}>
                      <Box
                        sx={{
                          maxWidth: '70%',
                          px: 2, py: 1,
                          borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          bgcolor: isMine ? 'primary.main' : '#f0f0f0',
                          color: isMine ? 'white' : 'text.primary',
                        }}>
                        <Typography variant="body2">{msg.body}</Typography>
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.7, display: 'block', textAlign: 'right', mt: 0.25, fontSize: 10 }}>
                          {fmtDate(msg.createdAt, 'datetime')}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

              {/* Compose */}
              <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type a message..."
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    multiline
                    maxRows={4}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={sending || !draft.trim()}
                    sx={{ alignSelf: 'flex-end', bgcolor: 'primary.main', color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' } }}>
                    {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  </IconButton>
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
